import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../services/subscriptionService';
import { AdminUser, SubscriptionStatus } from '../../types';
import { useState } from 'react';
import { GrantSubscriptionDialog } from './components/GrantSubscriptionDialog';
import { BanUserDialog } from './components/BanUserDialog';

const statusColor = (status?: SubscriptionStatus | null) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
    case SubscriptionStatus.ADMIN_GRANT: return 'success';
    case SubscriptionStatus.TRIALING: return 'info';
    case SubscriptionStatus.PAST_DUE: return 'warning';
    case SubscriptionStatus.CANCELED:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.BANNED: return 'error';
    default: return 'default';
  }
};

const fmt = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleDateString('fr-FR') : '—';

const fmtFull = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleString('fr-FR') : '—';

export const AdminUserDetailPage = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [grantOpen, setGrantOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => adminService.getUser(userId!),
    enabled: !!userId,
  });

  const { data: actions } = useQuery({
    queryKey: ['admin-actions', userId],
    queryFn: () => adminService.getActions(userId!, 1, 20),
    enabled: !!userId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const startTrialMutation = useMutation({
    mutationFn: () => adminService.startTrial(userId!),
    onSuccess: invalidate,
  });

  const revokeTrialMutation = useMutation({
    mutationFn: () => adminService.revokeTrial(userId!),
    onSuccess: invalidate,
  });

  const unbanMutation = useMutation({
    mutationFn: () => adminService.unbanUser(userId!),
    onSuccess: invalidate,
  });

  if (isLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (error || !user) return <Alert severity="error" sx={{ m: 3 }}>{t('admin.loadError')}</Alert>;

  const sub = user.subscription;
  const isTrialing = sub?.status === SubscriptionStatus.TRIALING;
  const isPendingTrial = sub?.status === SubscriptionStatus.PENDING_TRIAL;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/users')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {user.first_name} {user.last_name}
            {user.is_banned && (
              <Chip label={t('admin.users.banned')} color="error" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
        </Box>
        {/* Quick actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isPendingTrial && (
            <Tooltip title={t('admin.users.startTrial')}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                startIcon={startTrialMutation.isPending ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                onClick={() => startTrialMutation.mutate()}
                disabled={startTrialMutation.isPending}
              >
                {t('admin.users.startTrial')}
              </Button>
            </Tooltip>
          )}
          {isTrialing && (
            <Tooltip title={t('admin.users.revokeTrial')}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={revokeTrialMutation.isPending ? <CircularProgress size={14} /> : <TimerOffIcon />}
                onClick={() => revokeTrialMutation.mutate()}
                disabled={revokeTrialMutation.isPending}
              >
                {t('admin.users.revokeTrial')}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<CardGiftcardIcon />}
            onClick={() => setGrantOpen(true)}
          >
            {t('admin.users.grantSubscription')}
          </Button>
          {user.is_banned ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={unbanMutation.isPending ? <CircularProgress size={14} /> : <CheckCircleIcon />}
              onClick={() => unbanMutation.mutate()}
              disabled={unbanMutation.isPending}
            >
              {t('admin.users.unban')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<BlockIcon />}
              onClick={() => setBanOpen(true)}
            >
              {t('admin.users.ban')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Profile + subscription */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('admin.detail.profile')}
              </Typography>
              <InfoRow label={t('admin.detail.role')} value={user.role} />
              <InfoRow label={t('admin.detail.joined')} value={fmt(user.created_at)} />
              <InfoRow label={t('admin.detail.lastActive')} value={fmtFull(user.last_active_at)} />
              <InfoRow label={t('admin.detail.status')} value={user.is_active ? t('admin.detail.active') : t('admin.detail.inactive')} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('admin.detail.subscription')}
              </Typography>
              {sub ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={t(`subscription.status.${sub.status}`)}
                      color={statusColor(sub.status) as any}
                      size="small"
                    />
                    {sub.cancel_at_period_end && (
                      <Chip label={t('admin.detail.cancelAtEnd')} size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                  {sub.trial_start && <InfoRow label={t('admin.detail.trialStart')} value={fmt(sub.trial_start)} />}
                  {sub.trial_end && <InfoRow label={t('admin.detail.trialEnd')} value={fmt(sub.trial_end)} />}
                  {sub.current_period_end && <InfoRow label={t('admin.detail.periodEnd')} value={fmt(sub.current_period_end)} />}
                  {sub.trial_days_remaining !== null && sub.status === SubscriptionStatus.TRIALING && (
                    <InfoRow label={t('admin.detail.daysRemaining')} value={`${sub.trial_days_remaining} jours`} />
                  )}
                  {sub.stripe_customer_id && (
                    <InfoRow label="Stripe customer" value={sub.stripe_customer_id} mono />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Usage metrics */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('admin.detail.usageAllTime')}
              </Typography>
              {user.usage_all_time ? (
                <>
                  <InfoRow label={t('subscription.usageTranscriptions')} value={user.usage_all_time.ai_consultations_count} />
                  <InfoRow label={t('subscription.usageTranscriptionTime')} value={`${user.usage_all_time.transcription_minutes} min`} />
                  <InfoRow label={t('subscription.usageChat')} value={user.usage_all_time.chat_messages_count} />
                  <InfoRow label={t('subscription.usageSuggestions')} value={user.usage_all_time.ai_suggestions_count} />
                  <InfoRow label={t('subscription.usageReports')} value={user.usage_all_time.client_reports_count} />
                  <InfoRow label={t('admin.users.totalRequests')} value={user.total_ai_requests} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('subscription.noUsageData')}
                </Typography>
              )}
              {user.usage?.period_start && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
                    {t('admin.detail.usagePeriod')}: {fmt(user.usage.period_start)} → {fmt(user.usage.period_end)}
                  </Typography>
                  <InfoRow label={t('subscription.usageTranscriptions')} value={user.usage.ai_consultations_count} />
                  <InfoRow label={t('subscription.usageChat')} value={user.usage.chat_messages_count} />
                  <InfoRow label={t('subscription.usageReports')} value={user.usage.client_reports_count} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Admin action history */}
        {actions && actions.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('admin.detail.actionHistory')}
                </Typography>
                <TableContainer component={Paper} elevation={0} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('admin.detail.actionType')}</TableCell>
                        <TableCell>{t('admin.detail.actionDate')}</TableCell>
                        <TableCell>{t('admin.detail.actionNotes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actions.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            <Chip label={t(`admin.actionType.${a.action_type}`)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{fmtFull(a.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{a.notes || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {grantOpen && (
        <GrantSubscriptionDialog
          user={user as AdminUser}
          open
          onClose={() => setGrantOpen(false)}
          onSuccess={() => { setGrantOpen(false); invalidate(); }}
        />
      )}
      {banOpen && (
        <BanUserDialog
          user={user as AdminUser}
          open
          onClose={() => setBanOpen(false)}
          onSuccess={() => { setBanOpen(false); invalidate(); }}
        />
      )}
    </Box>
  );
};

const InfoRow = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? '0.75rem' : undefined }}>
      {value}
    </Typography>
  </Box>
);
